import { useQuery, useQueryClient } from "@tanstack/react-query";
import { create, windowScheduler, keyResolver } from "@yornaath/batshit";
import { useCompanyIdFromUrl } from "@/hooks/companies/useCompanyIdFromUrl";
import { fetchShareholdersByIds } from "@/hooks/shares/useShareholdersByIds";

// The endpoint is company scoped, so batches can only contain shareholders from the same company
const shareholderBatchers = new Map();

const getShareholderBatcher = (companyId) => {
  if (!shareholderBatchers.has(companyId)) {
    shareholderBatchers.set(
      companyId,
      create({
        fetcher: async (ids) => {
          const response = await fetchShareholdersByIds(companyId, ids, {
            simple: true,
          });
          return response.data;
        },
        resolver: keyResolver("id"),
        scheduler: windowScheduler(10),
      }),
    );
  }

  return shareholderBatchers.get(companyId);
};

const useShareholder = (investmentId, { companyId, ...queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const companyIdFromUrl = useCompanyIdFromUrl();
  const resolvedCompanyId = companyId ?? companyIdFromUrl;
  const shareholdersQueryKey = ["shareholders", resolvedCompanyId];

  return useQuery({
    enabled: Boolean(investmentId && resolvedCompanyId),
    queryKey: ["shareholder", resolvedCompanyId, investmentId],
    queryFn: async () => {
      const shareholder =
        await getShareholderBatcher(resolvedCompanyId).fetch(investmentId);
      return shareholder ?? null;
    },
    // Reuse the shareholder from the useShareholders list if it is already cached
    initialData: () =>
      queryClient
        .getQueryData(shareholdersQueryKey)
        ?.data?.find?.((shareholder) => shareholder.id === investmentId),
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(shareholdersQueryKey)?.dataUpdatedAt,
    staleTime: Infinity,
    ...queryOptions,
  });
};

export { useShareholder };
